import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, BaseSelect } from 'ui-kit';
import { BUTTON_TYPES } from '_constants';
import { size as BREAKPOINTS } from '_constants/DEVICES';
import { usePopover, useWindowDimensions } from 'hooks';
import * as Styled from './styles';

const DEFAULT_FORMATS = [
  { value: 'xlsx', label: 'Excel', sublabel: '.xlsx' },
  { value: 'csv', label: 'CSV', sublabel: '.csv' },
];

const PANEL_INITIAL_FOCUS_SELECTOR =
  'button[data-card="true"], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Clicks inside a BaseSelect dropdown menu (which may be portaled to body)
// should not close the popover. The data attribute is set by BaseSelect.
const isBaseSelectMenuTarget = (target) => !!target?.closest?.('[data-baseselect-menu]');

const resolveSubmitText = (submitText, format) => (typeof submitText === 'function' ? submitText(format) : submitText);

function ExportPopover({
  title,
  triggerText,
  formats,
  scope,
  onExport,
  onOpen,
  isDisabled,
  submitText,
  defaultValue,
  validate,
}) {
  const effectiveFormats = useMemo(
    () => (formats ? DEFAULT_FORMATS.filter((f) => formats.includes(f.value)) : DEFAULT_FORMATS),
    [formats],
  );

  const effectiveTriggerText = triggerText ?? 'Export';

  const initialValue = defaultValue ?? effectiveFormats[0]?.value ?? null;
  const [selected, setSelected] = useState(initialValue);
  const [submitting, setSubmitting] = useState(false);

  const { width: viewportWidth } = useWindowDimensions();
  const isMobile = viewportWidth <= parseInt(BREAKPOINTS.tablet, 10);

  const wrapperRef = useRef(null);
  const panelRef = useRef(null);
  const titleId = useMemo(() => `export-popover-title-${Math.random().toString(36).slice(2, 9)}`, []);

  const { open, setOpen, close } = usePopover({
    wrapperRef,
    panelRef,
    isExternalInside: isBaseSelectMenuTarget,
    initialFocusSelector: PANEL_INITIAL_FOCUS_SELECTOR,
  });

  const scopeVisible = !!scope && (scope.visibleFor ? scope.visibleFor(selected) : true);
  const isScopeEmpty = scopeVisible && (Array.isArray(scope.value) ? scope.value.length === 0 : scope.value == null);

  const mobilePanelStyle = useMemo(
    () =>
      isMobile
        ? {
            position: 'fixed',
            top: Styled.MOBILE_PANEL_TOP_OFFSET,
            left: '50%',
            right: 'auto',
            transform: 'translateX(-50%)',
            animation: 'none',
            maxHeight: `calc(100vh - ${Styled.MOBILE_PANEL_TOP_OFFSET * 2}px)`,
          }
        : undefined,
    [isMobile],
  );

  const toggleOpen = () => {
    if (isDisabled) return;
    setSelected(initialValue);
    setOpen((v) => {
      const next = !v;
      if (next && onOpen) onOpen();
      return next;
    });
  };

  const handleSubmit = async () => {
    const scopeValue = scopeVisible ? scope.value : undefined;
    if (validate) {
      const ok = await validate(selected, scopeValue);
      if (!ok) return;
    }
    try {
      setSubmitting(true);
      await onExport(selected, scopeValue);
      close();
    } catch {
      // Caller is responsible for surfacing the export failure (toast/notification);
      // we just keep the popover open so the user can retry.
    } finally {
      setSubmitting(false);
    }
  };

  const onCardKeyDown = (e, idx) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (idx + 1) % effectiveFormats.length;
      setSelected(effectiveFormats[next].value);
      const cards = panelRef.current?.querySelectorAll('button[data-card="true"]');
      cards?.[next]?.focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (idx - 1 + effectiveFormats.length) % effectiveFormats.length;
      setSelected(effectiveFormats[prev].value);
      const cards = panelRef.current?.querySelectorAll('button[data-card="true"]');
      cards?.[prev]?.focus();
    }
  };

  const renderScope = () => {
    if (!scope || !scopeVisible) return null;

    if (scope.kind === 'radio') {
      return (
        <Styled.RadioStack role="radiogroup" aria-label={scope.label}>
          {scope.options.map((opt) => {
            const isSelected = opt.value === scope.value;
            return (
              <Styled.RadioOption
                key={opt.value}
                role="radio"
                aria-checked={isSelected}
                selected={isSelected}
                onClick={() => scope.onChange(opt.value)}
              >
                <Styled.RadioDot selected={isSelected} aria-hidden="true" />
                <Styled.RadioContent>
                  <Styled.RadioLabel>{opt.label}</Styled.RadioLabel>
                  {opt.sublabel && <Styled.RadioSublabel>{opt.sublabel}</Styled.RadioSublabel>}
                </Styled.RadioContent>
              </Styled.RadioOption>
            );
          })}
        </Styled.RadioStack>
      );
    }

    if (scope.kind === 'select') {
      return (
        <BaseSelect
          value={scope.value}
          onChange={scope.onChange}
          inputId={scope.inputId || `export-popover-scope-${titleId}`}
          options={scope.options}
          isMulti={!!scope.isMulti}
          placeholder={scope.placeholder || 'Select…'}
          error={scope.error}
          className={scope.error ? 'error' : undefined}
          isExportVisit={!!scope.isExportVisit}
          allowSelectAll={!!scope.allowSelectAll}
          maxValueContainerHeight={scope.maxValueContainerHeight ?? (scope.isMulti ? 300 : null)}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition="fixed"
          objStyles={{ menuPortal: (base) => ({ ...base, zIndex: 1002 }) }}
        />
      );
    }

    return null;
  };

  const handlePrintClick = () => {
    if (isDisabled) return;
    onExport('print', undefined);
  };

  return (
    <Styled.Wrapper ref={wrapperRef} data-html2canvas-ignore>
      <Styled.IconTrigger onClick={handlePrintClick} disabled={isDisabled} aria-label="Print" title="Print">
        <svg width="24" height="24" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M5 1a1 1 0 0 0-1 1v2H2.5A1.5 1.5 0 0 0 1 5.5v5A1.5 1.5 0 0 0 2.5 12H4v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2h1.5a1.5 1.5 0 0 0 1.5-1.5v-5A1.5 1.5 0 0 0 13.5 4H12V2a1 1 0 0 0-1-1H5zm0 1h6v2H5V2zm0 8h6v4H5v-4zm7-3a.75.75 0 1 1 0 1.5.75.75 0 0 1 0-1.5z" />
        </svg>
      </Styled.IconTrigger>
      <Styled.Trigger
        onClick={toggleOpen}
        disabled={isDisabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={effectiveTriggerText}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M9.293 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.707A1 1 0 0 0 13.707 4L10 .293A1 1 0 0 0 9.293 0zM9.5 3.5v-2l3 3h-2a1 1 0 0 1-1-1zm-1 4v3.793l1.146-1.147a.5.5 0 0 1 .708.708l-2 2a.5.5 0 0 1-.708 0l-2-2a.5.5 0 0 1 .708-.708L7.5 11.293V7.5a.5.5 0 0 1 1 0z" />
        </svg>
        <span>{effectiveTriggerText}</span>
        <Styled.Caret width="9" height="5" viewBox="0 0 9 5" open={open} aria-hidden="true">
          <path d="M4.5 5L0.602885 0.5L8.39712 0.5L4.5 5Z" />
        </Styled.Caret>
      </Styled.Trigger>
      {open && (
        <Styled.Panel
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          style={mobilePanelStyle}
        >
          <Styled.ScrollBody>
            <Styled.Title id={titleId}>{title}</Styled.Title>
            <Styled.FieldLabel>Format</Styled.FieldLabel>
            <Styled.CardGrid count={effectiveFormats.length} role="radiogroup" aria-label="Export format">
              {effectiveFormats.map((opt, idx) => {
                const isSelected = opt.value === selected;
                return (
                  <Styled.Card
                    key={opt.value}
                    data-card="true"
                    role="radio"
                    aria-checked={isSelected}
                    selected={isSelected}
                    tabIndex={isSelected || (selected == null && idx === 0) ? 0 : -1}
                    onClick={() => setSelected(opt.value)}
                    onKeyDown={(e) => onCardKeyDown(e, idx)}
                  >
                    <Styled.CardLabel>{opt.label}</Styled.CardLabel>
                    {opt.sublabel && <Styled.CardSublabel>{opt.sublabel}</Styled.CardSublabel>}
                  </Styled.Card>
                );
              })}
            </Styled.CardGrid>
            {scope && scopeVisible && (
              <Styled.FieldGroup>
                <Styled.FieldLabel>{scope.label}</Styled.FieldLabel>
                {renderScope()}
              </Styled.FieldGroup>
            )}
          </Styled.ScrollBody>
          <Styled.SubmitWrapper>
            <Button onClick={close} text="Cancel" variant={BUTTON_TYPES.DARK} disabled={submitting} />
            <Button
              onClick={handleSubmit}
              text={resolveSubmitText(submitText, selected)}
              variant={BUTTON_TYPES.DANGER}
              disabled={submitting || selected == null || isScopeEmpty}
              loading={submitting}
            />
          </Styled.SubmitWrapper>
        </Styled.Panel>
      )}
    </Styled.Wrapper>
  );
}

const scopeShape = PropTypes.shape({
  kind: PropTypes.oneOf(['radio', 'select']).isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  isMulti: PropTypes.bool,
  visibleFor: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  placeholder: PropTypes.string,
  inputId: PropTypes.string,
  isExportVisit: PropTypes.bool,
  allowSelectAll: PropTypes.bool,
  maxValueContainerHeight: PropTypes.number,
});

ExportPopover.propTypes = {
  title: PropTypes.string,
  triggerText: PropTypes.string,
  formats: PropTypes.arrayOf(PropTypes.string),
  scope: scopeShape,
  onExport: PropTypes.func.isRequired,
  onOpen: PropTypes.func,
  isDisabled: PropTypes.bool,
  submitText: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  validate: PropTypes.func,
};

ExportPopover.defaultProps = {
  title: 'Export',
  triggerText: null,
  formats: null,
  scope: null,
  onOpen: null,
  isDisabled: false,
  submitText: 'Export',
  defaultValue: undefined,
  validate: null,
};

export default ExportPopover;
